import React from "react";
import { createRoot } from "react-dom/client";
import { FeedbackWidget } from "./feedback-widget";
import { WIDGET_BASE_CSS } from "./widget-styles";
import type { FeedbackWidgetProps } from "@/shared/feedback";

type PeeqbackQueueEntry = [method: string, ...args: unknown[]];

type WidgetBootstrapResponse = {
  workspace?: {
    slug?: string;
    widgetWorkspaceKey?: string;
  };
  widget?: {
    title?: string;
    buttonText?: string;
    buttonStyle?: FeedbackWidgetProps["buttonStyle"];
    brandColor?: string;
    logoUrl?: string;
    hideBranding?: boolean;
    position?: FeedbackWidgetProps["position"];
    successMessage?: string;
    submissionsEnabled?: boolean;
    allowGuestSubmissions?: boolean;
    showRoadmapLink?: boolean;
    apiBaseUrl?: string;
  };
  roadmap?: {
    url?: string;
  };
};

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    peeqbackSettings?: FeedbackWidgetProps;
    $peeqbackq?: PeeqbackQueueEntry[];
    peeqback?: {
      // eslint-disable-next-line no-unused-vars
      init: (config: FeedbackWidgetProps) => void;
      // eslint-disable-next-line no-unused-vars
      identify: (user: NonNullable<FeedbackWidgetProps["user"]>) => void;
      open: () => void;
    };
  }
}

function readScriptConfig(script: HTMLScriptElement): FeedbackWidgetProps {
  const data = script.dataset;
  const scriptConfig: FeedbackWidgetProps = {
    brandColor: data.brandColor,
    position: data.position as FeedbackWidgetProps["position"],
    logoUrl: data.logoUrl,
    hideBranding: data.hideBranding === "true",
    title: data.title,
    buttonText: data.buttonText,
    successMessage: data.successMessage,
    apiEndpoint: data.apiEndpoint,
    roadmapUrl: data.roadmapUrl,
    workspaceKey: data.workspaceKey,
    submissionsEnabled: data.submissionsEnabled !== "false",
    allowGuestSubmissions: data.allowGuestSubmissions !== "false",
    showRoadmapLink: data.showRoadmapLink !== "false",
    buttonStyle: data.buttonStyle as FeedbackWidgetProps["buttonStyle"],
  };

  const loaderConfig =
    typeof window !== "undefined" && window.peeqbackSettings
      ? window.peeqbackSettings
      : {};

  return {
    ...scriptConfig,
    ...loaderConfig,
    user: {
      ...scriptConfig.user,
      ...loaderConfig.user,
    },
  };
}

function mergeWidgetConfig(
  current: FeedbackWidgetProps,
  next: FeedbackWidgetProps
): FeedbackWidgetProps {
  return {
    ...current,
    ...next,
    user: {
      ...current.user,
      ...next.user,
    },
  };
}

async function fetchBootstrapConfig(
  script: HTMLScriptElement,
  config: FeedbackWidgetProps
): Promise<FeedbackWidgetProps> {
  const workspaceKey = config.workspaceKey?.trim();
  if (!workspaceKey) {
    return config;
  }

  let widgetOrigin = "";
  try {
    widgetOrigin = new URL(script.src, window.location.href).origin;
  } catch {
    return config;
  }

  const bootstrapUrl = new URL(`${widgetOrigin}/api/widget/bootstrap`);
  bootstrapUrl.searchParams.set("workspaceKey", workspaceKey);

  const response = await fetch(bootstrapUrl.toString());
  if (!response.ok) {
    throw new Error("Widget bootstrap failed.");
  }

  const payload = (await response.json()) as WidgetBootstrapResponse;
  return mergeWidgetConfig(config, {
    title: payload.widget?.title,
    buttonText: payload.widget?.buttonText,
    buttonStyle: payload.widget?.buttonStyle,
    brandColor: payload.widget?.brandColor,
    logoUrl: payload.widget?.logoUrl,
    hideBranding: payload.widget?.hideBranding,
    position: payload.widget?.position,
    successMessage: payload.widget?.successMessage,
    submissionsEnabled: payload.widget?.submissionsEnabled,
    allowGuestSubmissions: payload.widget?.allowGuestSubmissions,
    showRoadmapLink: payload.widget?.showRoadmapLink,
    apiEndpoint: payload.widget?.apiBaseUrl
      ? `${payload.widget.apiBaseUrl.replace(/\/+$/, "")}/feedback`
      : config.apiEndpoint,
    roadmapUrl: payload.roadmap?.url || config.roadmapUrl,
    workspaceKey: payload.workspace?.widgetWorkspaceKey || workspaceKey,
  });
}

function mountWidget() {
  const script = document.currentScript as HTMLScriptElement | null;
  const targetScript =
    script ||
    (document.querySelector('script[data-feedback-widget="true"]') as HTMLScriptElement | null);

  if (!targetScript) {
    return;
  }

  const initialConfig = readScriptConfig(targetScript);
  const hasDirectScriptConfig = targetScript.hasAttribute("data-feedback-widget");
  let currentConfig: FeedbackWidgetProps = initialConfig;
  let openSignal = 0;

  const existingHost = document.getElementById("peeqback-feedback-widget-root");
  const host = existingHost || document.createElement("div");
  if (!existingHost) {
    host.id = "peeqback-feedback-widget-root";
    document.body.appendChild(host);
  }

  const shadowRoot =
    host.shadowRoot || host.attachShadow({ mode: "open" });

  if (!shadowRoot.querySelector("style[data-peeqback-widget-style='true']")) {
    const style = document.createElement("style");
    style.dataset.peeqbackWidgetStyle = "true";
    style.textContent = WIDGET_BASE_CSS;
    shadowRoot.appendChild(style);
  }

  let mountNode = shadowRoot.querySelector("#peeqback-feedback-widget-mount") as HTMLDivElement | null;
  if (!mountNode) {
    mountNode = document.createElement("div");
    mountNode.id = "peeqback-feedback-widget-mount";
    shadowRoot.appendChild(mountNode);
  }

  const root = createRoot(mountNode);

  const renderWidget = () => {
    root.render(
      <React.StrictMode>
        <FeedbackWidget {...currentConfig} openSignal={openSignal} injectStyles={false} />
      </React.StrictMode>
    );
  };

  const renderWithBootstrap = async () => {
    try {
      currentConfig = await fetchBootstrapConfig(targetScript, currentConfig);
    } catch {
      if (currentConfig.workspaceKey) {
        return;
      }
    }

    renderWidget();
  };

  const api = {
    init(config: FeedbackWidgetProps) {
      currentConfig = mergeWidgetConfig(currentConfig, config);
      void renderWithBootstrap();
    },
    identify(user: NonNullable<FeedbackWidgetProps["user"]>) {
      currentConfig = mergeWidgetConfig(currentConfig, { user });
      renderWidget();
    },
    open() {
      openSignal += 1;
      renderWidget();
    },
  };

  window.peeqback = api;

  if (hasDirectScriptConfig || window.peeqbackSettings) {
    void renderWithBootstrap();
  }

  const queuedCalls = Array.isArray(window.$peeqbackq) ? [...window.$peeqbackq] : [];
  window.$peeqbackq = [];

  for (const [method, ...args] of queuedCalls) {
    if (method === "init" && args[0] && typeof args[0] === "object") {
      api.init(args[0] as FeedbackWidgetProps);
    } else if (method === "identify" && args[0] && typeof args[0] === "object") {
      api.identify(args[0] as NonNullable<FeedbackWidgetProps["user"]>);
    } else if (method === "open") {
      api.open();
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountWidget, { once: true });
} else {
  mountWidget();
}
